import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, RefreshCw, Trash2, CheckCircle2, Plug, FileSpreadsheet, UploadCloud, Loader2, AlertCircle, CreditCard, Info, X, Smartphone, Monitor, ExternalLink, Receipt as ReceiptIcon, FileText, Calendar, Tag, Building2, Zap, ShieldCheck, Sparkles } from "lucide-react";
import api from "@/lib/api";

function fmtDate(iso) {
    if (!iso) return "Jamais";
    try {
        return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
}

function ProviderLogo({ id }) {
    const map = {
        gmail: <Mail className="w-5 h-5 text-[#EA4335]" />,
        outlook: <Mail className="w-5 h-5 text-[#0078D4]" />,
    };
    return map[id] || <Mail className="w-5 h-5" />;
}

function SectionBadge({ status, label }) {
    const styles = {
        active: "bg-[#34C759]/15 text-[#34C759]",
        partial: "bg-[#FF9F0A]/15 text-[#FF9F0A]",
        pending: "bg-[#86868B]/15 text-[#86868B]",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${styles[status] || styles.pending}`}>
            {label}
        </span>
    );
}

function QuotaBar({ label, used, limit, testId }) {
    const pct = Math.min(100, Math.round((used / limit) * 100));
    const color = pct >= 100 ? "#FF3B30" : pct >= 75 ? "#FF9F0A" : "#007AFF";
    return (
        <div className="flex-1 min-w-[180px]" data-testid={testId}>
            <div className="flex items-baseline justify-between">
                <p className="text-xs font-medium text-[#1D1D1F]">{label}</p>
                <p className="text-xs font-semibold tabular-nums" style={{ color }}>
                    {used}&nbsp;/&nbsp;{limit}
                </p>
            </div>
            <div className="mt-1.5 h-1.5 w-full rounded-full bg-black/[0.07] overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
            </div>
        </div>
    );
}

function QuotaCounter({ quotas }) {
    if (!quotas || (quotas.statement_limit == null && quotas.transaction_limit == null)) return null;
    const statementsFull = quotas.statement_limit != null && quotas.statements_used >= quotas.statement_limit;
    const txFull = quotas.transaction_limit != null && quotas.transactions_used >= quotas.transaction_limit;
    const full = statementsFull || txFull;
    return (
        <div
            className={`mt-4 rounded-2xl border p-4 flex flex-wrap items-center gap-x-6 gap-y-3 ${
                full ? "border-[#FF9F0A]/40 bg-[#FF9F0A]/[0.06]" : "border-black/5 bg-white/60"
            }`}
            data-testid="quota-counter"
        >
            <p className="text-xs font-bold uppercase tracking-wider text-[#86868B] whitespace-nowrap">
                Plan {quotas.plan_name}
            </p>
            {quotas.statement_limit != null && (
                <QuotaBar
                    label="Relevés bancaires"
                    used={quotas.statements_used}
                    limit={quotas.statement_limit}
                    testId="quota-statements"
                />
            )}
            {quotas.transaction_limit != null && (
                <QuotaBar
                    label={quotas.transaction_period === "month" ? "Transactions justifiées ce mois-ci" : "Transactions justifiées"}
                    used={quotas.transactions_used}
                    limit={quotas.transaction_limit}
                    testId="quota-transactions"
                />
            )}
            <Link
                to="/app/forfait"
                className="text-xs font-semibold text-[#007AFF] hover:underline whitespace-nowrap ml-auto"
                data-testid="quota-upgrade-link"
            >
                {full ? "Limite atteinte — passer au Pro →" : "Passer au Pro →"}
            </Link>
        </div>
    );
}

export default function Connections() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [csvDragOver, setCsvDragOver] = useState(false);
    const [csvUploading, setCsvUploading] = useState(false);
    const csvInputRef = useRef(null);
    const [busy, setBusy] = useState(null);
    const [quotas, setQuotas] = useState(null);
    const [showRevolutGuide, setShowRevolutGuide] = useState(false);
    const [traceModal, setTraceModal] = useState(null); // connection_id or null

    const refresh = async () => {
        const { data } = await api.get("/connections");
        setData(data);
        api.get("/quotas").then((r) => setQuotas(r.data)).catch(() => {});
    };

    // Detect OAuth callback on mount: ?code=...&state=... or ?simulated=1&provider=...
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const state = params.get("state");
        const simulated = params.get("simulated") === "1";
        const provider = params.get("provider") || "gmail";
        const error = params.get("error");

        if (error) {
            toast.error(`Autorisation refusée: ${error}`);
            window.history.replaceState({}, "", "/app/connections");
            refresh();
            return;
        }

        if (code || simulated) {
            (async () => {
                const tid = toast.loading("Finalisation de la connexion…");
                try {
                    await api.post("/connections/email/callback", {
                        provider,
                        code: code || undefined,
                        state: state || undefined,
                        redirect_uri: window.location.origin + "/app/connections",
                        simulated,
                    });
                    toast.success(`${provider} connecté !`, { id: tid });
                    window.history.replaceState({}, "", "/app/connections");
                    refresh();
                } catch (e) {
                    toast.error(e?.response?.data?.detail || "Échec de la connexion", { id: tid });
                    window.history.replaceState({}, "", "/app/connections");
                }
            })();
        } else {
            refresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleConnect = async (provider) => {
        setBusy(`connect-${provider}`);
        try {
            const { data: link } = await api.post("/connections/email/connect", {
                provider,
                redirect_uri: window.location.origin + "/app/connections",
            });
            if (link.simulated) {
                await api.post("/connections/email/callback", { provider, simulated: true });
                toast.success(`${provider} connecté en mode démo`);
                refresh();
            } else {
                window.location.href = link.oauth_url;
            }
        } catch {
            toast.error("Échec de connexion");
        } finally {
            setBusy(null);
        }
    };

    const [rangeModal, setRangeModal] = useState(null); // {connection_id} or null

    const handleSync = async (id, opts = {}) => {
        setBusy(`sync-${id}`);
        const tid = toast.loading("Scan des emails en cours…");
        try {
            const params = {};
            if (opts.since) params.since_iso = opts.since;
            if (opts.until) params.until_iso = opts.until;
            const { data } = await api.post(`/connections/${id}/sync`, null, { params });
            toast.success(`${data.imported} e-reçu(s) importé(s)`, { id: tid });
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Échec du scan", { id: tid });
        } finally {
            setBusy(null);
        }
    };

    const handleSyncAll = async () => {
        const conns = data?.email_connections || [];
        if (conns.length === 0) return;
        setBusy("sync-all");
        const tid = toast.loading(`Scan de ${conns.length} boîte${conns.length > 1 ? "s" : ""} mail…`);
        let totalImported = 0;
        let failed = 0;
        for (const c of conns) {
            try {
                const { data: r } = await api.post(`/connections/${c.connection_id}/sync`, null);
                totalImported += r.imported || 0;
            } catch {
                failed += 1;
            }
        }
        if (failed === conns.length) {
            toast.error("Toutes les boîtes ont échoué", { id: tid });
        } else if (failed > 0) {
            toast.success(`${totalImported} e-reçu(s) · ${failed} boîte(s) en erreur`, { id: tid });
        } else {
            toast.success(`${totalImported} e-reçu(s) importé(s) au total`, { id: tid });
        }
        // Relancer automatiquement le matching sur les transactions
        // pour que les nouveaux reçus se rattachent aux paiements existants
        try {
            await api.post("/transactions/rematch-all");
        } catch (err) {
            // Rematch-all is a best-effort follow-up to the sync — failing
            // here shouldn't surface a toast to the user (they just got a
            // success "X e-reçu(s) importé(s)"). Logged for dev visibility.
            if (process.env.NODE_ENV !== "production") {
                console.warn("Auto-rematch after sync failed:", err);
            }
        }
        refresh();
        setBusy(null);
    };

    const handleDelete = async (id) => {
        if (!confirm("Déconnecter cette boîte mail ?")) return;
        await api.delete(`/connections/${id}`);
        toast.success("Connexion supprimée");
        refresh();
    };

    const [diagModal, setDiagModal] = useState(null);

    const handleReparseAll = async () => {
        setBusy("reparse-all");
        const tid = toast.loading("GPT-5.2 réanalyse vos reçus…");
        try {
            const { data } = await api.post("/receipts/reparse-all");
            toast.success(
                `${data.updated} reçu(s) ré-extrait(s) · ${data.skipped} déjà OK`,
                { id: tid },
            );
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Échec de la ré-analyse", { id: tid });
        } finally {
            setBusy(null);
        }
    };

    const handleDiagnose = async (id) => {
        setBusy(`diag-${id}`);
        const tid = toast.loading("Diagnostic en cours…");
        try {
            const { data } = await api.post(`/connections/${id}/diagnose`);
            toast.success(`${data.messages_found} email${data.messages_found > 1 ? "s" : ""} trouvé${data.messages_found > 1 ? "s" : ""} par Gmail`, { id: tid });
            setDiagModal(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Échec du diagnostic", { id: tid });
        } finally {
            setBusy(null);
        }
    };

    const handleCsv = async (file) => {
        if (!file) return;
        if (!file.name.match(/\.(csv|tsv|txt|pdf)$/i)) {
            toast.error("Format non supporté. Exportez un fichier CSV ou PDF depuis votre banque.");
            return;
        }
        const isPdf = /\.pdf$/i.test(file.name);
        setCsvUploading(true);
        const tid = toast.loading(isPdf ? "Analyse du relevé PDF…" : "Analyse du relevé bancaire…");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data: r } = await api.post("/transactions/import-csv", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (r.imported === 0) {
                toast.info(r.message || "Aucune nouvelle transaction (déjà importée ?)", { id: tid });
            } else {
                toast.success(
                    `${r.imported} importées · ${r.matched} justifiées · ${r.missing} manquantes`,
                    { id: tid, duration: 6000 },
                );
            }
            api.get("/quotas").then((q) => setQuotas(q.data)).catch(() => {});
        } catch (e) {
            if (e?.response?.status === 402) {
                toast.error(e?.response?.data?.detail || "Limite du plan atteinte", {
                    id: tid,
                    duration: 10000,
                    action: {
                        label: "Passer au Pro",
                        onClick: () => navigate("/app/forfait"),
                    },
                });
            } else {
                toast.error(e?.response?.data?.detail || "Échec de l'import", { id: tid });
            }
        } finally {
            setCsvUploading(false);
        }
    };

    if (!data) return <p className="text-center text-[#86868B] py-12">Chargement…</p>;

    const emailStatus = data.email_connections.length > 0 ? "active" : "pending";

    return (
        <div className="max-w-5xl mx-auto px-6 sm:px-8 space-y-12">
            <div className="tk-rise">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">Automatisation</p>
                <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">Connexions</h1>
                <p className="mt-3 text-[#86868B] max-w-2xl leading-relaxed">
                    Reliez votre banque + boîte mail. 2 connexions suffisent pour automatiser 75 % de vos justificatifs.
                </p>
            </div>

            {/* SECTION 1 : BANQUE — Import CSV (premier plan) */}
            <section className="tk-rise tk-rise-1">
                <div className="flex items-end justify-between mb-2">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h2 className="text-xl font-semibold tracking-tight text-[#1D1D1F]">1. Banque (CSV ou PDF)</h2>
                            <SectionBadge
                                status="active"
                                label="Compatible toutes banques EU"
                            />
                        </div>
                        <p className="text-sm text-[#86868B]">
                            Téléchargez votre relevé CSV ou PDF depuis votre banque, glissez-le ici. Détection auto du format · Multi-devises.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowRevolutGuide(true)}
                        className="text-xs font-semibold text-[#007AFF] hover:underline whitespace-nowrap"
                        data-testid="revolut-guide-btn"
                    >
                        Guide Revolut →
                    </button>
                </div>

                <QuotaCounter quotas={quotas} />

                <div
                    onDragOver={(e) => { e.preventDefault(); setCsvDragOver(true); }}
                    onDragLeave={() => setCsvDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setCsvDragOver(false);
                        if (e.dataTransfer.files?.[0]) handleCsv(e.dataTransfer.files[0]);
                    }}
                    onClick={() => !csvUploading && csvInputRef.current?.click()}
                    data-testid="csv-upload-zone"
                    className={`mt-4 rounded-3xl p-10 text-center transition-all cursor-pointer flex flex-col items-center gap-4 border-2 border-dashed ${
                        csvDragOver ? "border-[#007AFF] bg-[#007AFF]/5 scale-[1.01]" : "border-[#AEAEB2] bg-white/50 hover:bg-white hover:border-[#1D1D1F]/30"
                    }`}
                >
                    <input
                        ref={csvInputRef}
                        type="file"
                        accept=".csv,.tsv,.txt,.pdf,text/csv,application/pdf"
                        className="hidden"
                        onChange={(e) => handleCsv(e.target.files?.[0])}
                        data-testid="csv-input"
                    />
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#007AFF]/12 to-[#34C759]/12 flex items-center justify-center">
                        {csvUploading
                            ? <Loader2 className="w-6 h-6 text-[#1D1D1F] animate-spin" />
                            : <FileSpreadsheet className="w-7 h-7 text-[#1D1D1F]" />}
                    </div>
                    <div>
                        <p className="text-lg font-semibold text-[#1D1D1F]">
                            {csvUploading ? "Import en cours…" : "Déposez votre relevé bancaire (CSV ou PDF)"}
                        </p>
                        <p className="mt-1 text-sm text-[#86868B]">
                            Glissez-déposez ou cliquez pour choisir un fichier
                        </p>
                    </div>
                    {!csvUploading && (
                        <>
                            <button className="tk-btn tk-btn-primary text-sm" data-testid="csv-pick-btn">
                                <UploadCloud className="w-4 h-4" /> Choisir un fichier (CSV / PDF)
                            </button>
                            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-2 text-[11px] font-medium text-[#86868B]">
                                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#34C759]" /> Revolut</span>
                                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#34C759]" /> Qonto</span>
                                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#34C759]" /> BNP / SG / CA / LCL</span>
                                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#34C759]" /> Boursorama / Fortuneo</span>
                                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-[#34C759]" /> Stripe / Wise</span>
                            </div>
                            <p className="text-[11px] text-[#AEAEB2] mt-1">
                            Détection auto du format · Multi-devises
                            </p>
                        </>
                    )}
                </div>
            </section>

            {/* SECTION 2 : EMAIL */}
            <section className="tk-rise tk-rise-2">
                <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h2 className="text-xl font-semibold tracking-tight text-[#1D1D1F]">2. Boîte mail</h2>
                            <SectionBadge status={emailStatus} label={emailStatus === "active" ? "Connecté" : "Non connecté"} />
                        </div>
                        <p className="text-sm text-[#86868B]">
                            Scan auto des e-reçus (Amazon, Uber, SNCF…) attachés aux transactions.
                        </p>
                    </div>
                    {data.email_connections.length >= 1 && (
                        <div className="flex flex-wrap gap-2">
                            {data.email_connections.length >= 2 && (
                                <button
                                    onClick={handleSyncAll}
                                    disabled={busy === "sync-all"}
                                    className="tk-btn tk-btn-blue text-sm"
                                    data-testid="sync-all-btn"
                                    title={`Scanner les ${data.email_connections.length} boîtes en une fois et rematcher les transactions`}
                                >
                                    {busy === "sync-all" ? (
                                        <><Loader2 className="w-4 h-4 animate-spin" /> Scan global…</>
                                    ) : (
                                        <><RefreshCw className="w-4 h-4" /> Tout scanner ({data.email_connections.length})</>
                                    )}
                                </button>
                            )}
                            <button
                                onClick={handleReparseAll}
                                disabled={busy === "reparse-all"}
                                className="tk-btn tk-btn-secondary text-sm"
                                data-testid="reparse-all-btn"
                                title="Re-extraire le marchand, montant, etc. avec GPT-5.2 pour les reçus mal parsés"
                            >
                                {busy === "reparse-all" ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> Ré-analyse…</>
                                ) : (
                                    <><Sparkles className="w-4 h-4" /> Ré-analyser avec IA</>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                <div className="mt-4 space-y-3">
                    {data.email_connections.map((c) => (
                        <div key={c.connection_id} className="tk-card p-4 flex flex-wrap items-center gap-4" data-testid={`conn-${c.connection_id}`}>
                            <ProviderLogo id={c.provider} />
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-[#1D1D1F] truncate">{c.email_address}</p>
                                <p className="text-xs text-[#86868B] capitalize">
                                    {c.provider} · {c.simulated ? "Mode démo" : "OAuth"} · Dernière sync : {fmtDate(c.last_sync_at)}
                                </p>
                            </div>
                            <button
                                onClick={() => setTraceModal(c.connection_id)}
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-[#34C759]/15 text-[#34C759] font-semibold hover:bg-[#34C759]/25 hover:scale-[1.04] active:scale-95 transition"
                                data-testid={`conn-trace-${c.connection_id}`}
                                title="Voir les reçus importés depuis cette boîte"
                            >
                                <CheckCircle2 className="w-3.5 h-3.5" /> {c.sync_count} reçus
                            </button>
                            <button
                                onClick={() => handleSync(c.connection_id)}
                                disabled={busy === `sync-${c.connection_id}`}
                                className="tk-btn tk-btn-secondary text-sm"
                                data-testid={`sync-btn-${c.connection_id}`}
                            >
                                <RefreshCw className={`w-4 h-4 ${busy === `sync-${c.connection_id}` ? "animate-spin" : ""}`} /> Scanner
                            </button>
                            <button
                                onClick={() => setRangeModal({ connection_id: c.connection_id })}
                                disabled={busy === `sync-${c.connection_id}`}
                                title="Scanner les emails d'une période précise"
                                className="tk-btn tk-btn-secondary text-sm"
                                data-testid={`range-btn-${c.connection_id}`}
                            >
                                <Calendar className="w-4 h-4" /> Période
                            </button>
                            <button
                                onClick={() => handleDiagnose(c.connection_id)}
                                disabled={busy === `diag-${c.connection_id}`}
                                title="Voir pourquoi certains emails ne sont pas convertis en reçus"
                                className="tk-btn tk-btn-secondary text-sm"
                                data-testid={`diag-btn-${c.connection_id}`}
                            >
                                {busy === `diag-${c.connection_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
                                <span className="hidden sm:inline">Diagnostic</span>
                            </button>
                            <button
                                onClick={() => handleDelete(c.connection_id)}
                                className="tk-btn tk-btn-secondary text-sm"
                                data-testid={`disconnect-btn-${c.connection_id}`}
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>

                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                    {data.available_email_providers.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => handleConnect(p.id)}
                            disabled={busy === `connect-${p.id}`}
                            className="tk-card p-4 flex items-center gap-3 text-left hover:tk-shadow-elev transition"
                            data-testid={`connect-${p.id}-btn`}
                        >
                            <ProviderLogo id={p.id} />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#1D1D1F]">Connecter {p.name}</p>
                                <p className="text-xs text-[#86868B]">
                                    {p.configured ? "OAuth officiel · 1 clic" : "Mode démo (OAuth réel en attente de clés)"}
                                </p>
                            </div>
                            <Plug className="w-4 h-4 text-[#86868B]" />
                        </button>
                    ))}
                </div>

                <div className="mt-4 p-4 bg-[#F5F5F7] rounded-2xl flex gap-3">
                    <Info className="w-4 h-4 text-[#86868B] flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-[#86868B] leading-relaxed">
                        <strong>Confidentialité :</strong> portée <code className="bg-white px-1.5 py-0.5 rounded">readonly</code>. On ne lit que les emails contenant "reçu", "facture", "votre commande" — jamais vos emails personnels. Déconnectable à tout moment.
                    </p>
                </div>
            </section>

            {/* SECTION 3 : POS connectés (à venir) */}
            <section className="tk-rise tk-rise-3">
                <div className="mb-2">
                    <div className="flex items-center gap-3 mb-1">
                        <h2 className="text-xl font-semibold tracking-tight text-[#1D1D1F]">3. Caisses marchandes connectées</h2>
                        <SectionBadge status="pending" label="À venir" />
                    </div>
                    <p className="text-sm text-[#86868B]">
                        Quand un commerce utilise Sunday, Yavin ou Lyf Pay, le ticket digital arrive directement sans email.
                    </p>
                </div>

                <div className="mt-4 grid sm:grid-cols-3 gap-3">
                    {[
                        { name: "Sunday", desc: "Restos QR-code" },
                        { name: "Yavin", desc: "Commerces" },
                        { name: "Lyf Pay", desc: "Multi-secteurs" },
                    ].map((p) => (
                        <div key={p.name} className="tk-card p-4 opacity-60" data-testid={`pos-${p.name.toLowerCase()}`}>
                            <p className="font-medium text-[#1D1D1F]">{p.name}</p>
                            <p className="text-xs text-[#86868B] mt-1">{p.desc}</p>
                            <p className="text-[10px] uppercase tracking-wider text-[#86868B] mt-3">Bientôt disponible</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Limites */}
            <section className="tk-rise tk-rise-4 tk-card p-6">
                <div className="flex items-start gap-4">
                    <AlertCircle className="w-5 h-5 text-[#FF9F0A] flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-semibold text-[#1D1D1F] mb-2">Limite physique honnête</p>
                        <p className="text-sm text-[#86868B] leading-relaxed">
                            Pour ~20 % de vos paiements (petits commerces sans e-reçu, paiements en espèces), il n'existe aucune source numérique. Vous verrez ces transactions marquées "Ticket papier probable" — il suffit alors de 7 secondes pour scanner le ticket avec votre téléphone. L'IA en extrait tout le contenu.
                        </p>
                    </div>
                </div>
            </section>

            <RevolutGuideModal open={showRevolutGuide} onClose={() => setShowRevolutGuide(false)} />
            <ConnectionTraceModal
                connectionId={traceModal}
                onClose={() => setTraceModal(null)}
            />
            <DiagnosticModal data={diagModal} onClose={() => setDiagModal(null)} />
            <DateRangeScanModal
                conn={rangeModal}
                onClose={() => setRangeModal(null)}
                onConfirm={(opts) => {
                    if (rangeModal) handleSync(rangeModal.connection_id, opts);
                    setRangeModal(null);
                }}
            />
        </div>
    );
}


// ---------- Revolut CSV export guide modal ----------
function RevolutGuideModal({ open, onClose }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center px-4 sm:px-6"
            role="dialog"
            aria-modal="true"
            data-testid="revolut-guide-modal"
        >
            <button
                aria-label="Fermer"
                onClick={onClose}
                className="absolute inset-0 bg-black/55 backdrop-blur-sm"
            />
            <div className="relative tk-card max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6 sm:p-8 tk-menu-pop">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-[#86868B] hover:text-[#1D1D1F]"
                    aria-label="Fermer"
                    data-testid="revolut-guide-close"
                >
                    <X className="w-5 h-5" />
                </button>

                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">Import Revolut</p>
                <h3 className="mt-1 text-2xl font-semibold tracking-tight text-[#1D1D1F]">
                    Exporter votre relevé en 30 secondes
                </h3>
                <p className="mt-2 text-sm text-[#86868B]">
                    Revolut perso et Business — la procédure est identique.
                </p>

                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <GuideCard
                        icon={Smartphone}
                        title="Depuis l'app mobile"
                        steps={[
                            "Ouvrez l'app Revolut",
                            "Allez dans Compte → Relevés",
                            'Choisissez "Excel (.xlsx)" ou "CSV"',
                            "Sélectionnez la période (mois en cours)",
                            "Tapez Générer → enregistrez sur le téléphone",
                            "Importez-le ici sur Ticketify",
                        ]}
                    />
                    <GuideCard
                        icon={Monitor}
                        title="Depuis le web"
                        steps={[
                            "Connectez-vous sur app.revolut.com",
                            "Allez dans Comptes → Relevé",
                            'Choisissez le format "CSV"',
                            "Sélectionnez vos comptes et la période",
                            "Cliquez Télécharger",
                            "Glissez le fichier ici",
                        ]}
                    />
                </div>

                <div className="mt-6 p-4 rounded-2xl bg-gradient-to-br from-[#007AFF]/[0.06] to-[#34C759]/[0.06] border border-[#007AFF]/15">
                    <p className="text-sm text-[#1D1D1F]">
                        <strong>Astuce&nbsp;:</strong> exportez un fichier <strong>par mois</strong> pour
                        recharger facilement vos nouvelles transactions. Ticketify ignore automatiquement les
                        doublons.
                    </p>
                </div>

                <div className="mt-5 flex items-start gap-2.5 text-xs text-[#86868B]">
                    <Info className="w-3.5 h-3.5 text-[#86868B] mt-0.5 shrink-0" />
                    <p>
                        Pourquoi pas une connexion automatique&nbsp;? Revolut perso n&apos;a pas d&apos;API
                        publique. Pour la sync auto temps réel, ça passera par un agrégateur bancaire (Bridge,
                        Tink) — disponible dans une prochaine version. En attendant, l&apos;import CSV prend
                        moins d&apos;une minute par mois.
                    </p>
                </div>

                <div className="mt-7 flex justify-end gap-3">
                    <button onClick={onClose} className="tk-btn tk-btn-secondary text-sm" data-testid="revolut-guide-got-it">
                        J&apos;ai compris
                    </button>
                </div>
            </div>
        </div>
    );
}

function GuideCard({ icon: Icon, title, steps }) {
    return (
        <div className="tk-card p-5">
            <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-xl bg-[#F5F5F7] border border-black/5 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#1D1D1F]" />
                </div>
                <h4 className="text-sm font-semibold text-[#1D1D1F]">{title}</h4>
            </div>
            <ol className="space-y-2">
                {steps.map((s, idx) => (
                    <li key={s} className="flex items-start gap-2.5 text-sm text-[#1D1D1F]/90 leading-snug">
                        <span className="shrink-0 w-5 h-5 rounded-full bg-[#1D1D1F] text-white text-[10px] font-bold flex items-center justify-center mt-0.5">
                            {idx + 1}
                        </span>
                        <span>{s}</span>
                    </li>
                ))}
            </ol>
        </div>
    );
}


// ---------- Connection traceability modal ----------
function ConnectionTraceModal({ connectionId, onClose }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!connectionId) return;
        setLoading(true);
        setError(null);
        setData(null);
        api.get(`/connections/${connectionId}/receipts`, { params: { limit: 200 } })
            .then((r) => setData(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Échec du chargement"))
            .finally(() => setLoading(false));
    }, [connectionId]);

    useEffect(() => {
        if (!connectionId) return;
        const esc = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", esc);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", esc);
            document.body.style.overflow = prev;
        };
    }, [connectionId, onClose]);

    if (!connectionId) return null;

    return (
        <div
            className="fixed inset-0 z-[90] flex items-center justify-center px-4 sm:px-6 tk-fade-in"
            role="dialog"
            aria-modal="true"
            data-testid="connection-trace-modal"
        >
            <button
                aria-label="Fermer"
                onClick={onClose}
                className="absolute inset-0 bg-black/55 backdrop-blur-sm"
            />
            <div className="relative tk-card max-w-3xl w-full max-h-[88vh] flex flex-col p-0 tk-menu-pop overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-black/5">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-2xl bg-[#EA4335]/10 flex items-center justify-center shrink-0">
                            <Mail className="w-5 h-5 text-[#EA4335]" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-[#86868B]">Traçabilité des reçus</p>
                            <h3 className="text-lg font-semibold tracking-tight text-[#1D1D1F] truncate">
                                {data?.connection?.email_address || "Connexion email"}
                            </h3>
                            <p className="text-xs text-[#86868B] mt-0.5">
                                {data?.count ?? 0} reçu{(data?.count ?? 0) > 1 ? "s" : ""} importé{(data?.count ?? 0) > 1 ? "s" : ""}
                                {data?.connection?.last_sync_at && ` · Dernière sync : ${fmtDate(data.connection.last_sync_at)}`}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Fermer"
                        className="w-9 h-9 rounded-full hover:bg-[#F5F5F7] flex items-center justify-center text-[#86868B] hover:text-[#1D1D1F] transition shrink-0"
                        data-testid="connection-trace-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading && (
                        <div className="py-12 flex flex-col items-center justify-center text-[#86868B]">
                            <Loader2 className="w-6 h-6 animate-spin mb-3" />
                            <p className="text-sm">Chargement des reçus…</p>
                        </div>
                    )}
                    {error && (
                        <div className="py-12 flex flex-col items-center justify-center text-[#FF3B30]">
                            <AlertCircle className="w-6 h-6 mb-3" />
                            <p className="text-sm">{error}</p>
                        </div>
                    )}
                    {!loading && !error && data && data.receipts?.length === 0 && (
                        <div className="py-12 flex flex-col items-center justify-center text-[#86868B]">
                            <Mail className="w-10 h-10 mb-3 opacity-40" />
                            <p className="text-sm">Aucun reçu importé pour le moment.</p>
                            <p className="text-xs mt-1">Cliquez sur « Scanner » pour relancer l&apos;analyse.</p>
                        </div>
                    )}
                    {!loading && !error && data && data.receipts?.length > 0 && (
                        <div className="space-y-2">
                            {data.receipts.map((r) => (
                                <TraceRow key={r.receipt_id} receipt={r} onClose={onClose} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-black/5 text-[11px] text-[#86868B] flex items-center gap-2">
                    <Info className="w-3.5 h-3.5 shrink-0" />
                    <span>Chaque ligne montre l&apos;email d&apos;origine et le justificatif qui en a été extrait. Cliquez pour ouvrir le reçu.</span>
                </div>
            </div>
        </div>
    );
}

function TraceRow({ receipt, onClose }) {
    const date = receipt.purchase_date
        ? new Date(receipt.purchase_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
        : "—";
    const amount = receipt.total_amount != null
        ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: receipt.currency || "EUR" }).format(receipt.total_amount)
        : "—";
    return (
        <Link
            to={`/app/receipt/${receipt.receipt_id}`}
            onClick={onClose}
            className="group flex items-center gap-4 p-3 rounded-2xl hover:bg-[#F5F5F7] transition border border-transparent hover:border-black/5"
            data-testid={`trace-row-${receipt.receipt_id}`}
        >
            {/* Email source */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[11px] font-medium text-[#86868B] mb-1">
                    <Mail className="w-3 h-3" />
                    <span className="truncate">{receipt.email_from || "Expéditeur inconnu"}</span>
                </div>
                <p className="text-sm font-medium text-[#1D1D1F] truncate" title={receipt.email_subject || ""}>
                    {receipt.email_subject || "Email sans objet"}
                </p>
                <div className="flex items-center gap-3 text-[11px] text-[#86868B] mt-1">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {date}</span>
                    <span className="inline-flex items-center gap-1 capitalize"><Tag className="w-3 h-3" /> {receipt.category || "autre"}</span>
                    {receipt.original_pdf_filename && (
                        <span className="inline-flex items-center gap-1 text-[#34C759]"><FileText className="w-3 h-3" /> PDF marchand</span>
                    )}
                </div>
            </div>

            {/* Arrow → */}
            <div className="hidden sm:flex flex-col items-center text-[#86868B] shrink-0">
                <ReceiptIcon className="w-4 h-4" />
            </div>

            {/* Receipt summary */}
            <div className="text-right shrink-0">
                <p className="text-base font-semibold text-[#1D1D1F]">{amount}</p>
                <p className="text-[11px] text-[#86868B] truncate max-w-[150px]">{receipt.merchant || "—"}</p>
            </div>

            <ExternalLink className="w-3.5 h-3.5 text-[#86868B] opacity-0 group-hover:opacity-100 transition shrink-0" />
        </Link>
    );
}


// ---------- Diagnostic modal ----------
function DiagnosticModal({ data, onClose }) {
    useEffect(() => {
        if (!data) return;
        const esc = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", esc);
        return () => document.removeEventListener("keydown", esc);
    }, [data, onClose]);

    if (!data) return null;
    const ok = (data.samples || []).filter((s) => s.ok);
    const ko = (data.samples || []).filter((s) => !s.ok);

    return (
        <div
            className="fixed inset-0 z-[90] flex items-center justify-center px-4 sm:px-6 tk-fade-in"
            role="dialog"
            aria-modal="true"
            data-testid="diagnostic-modal"
        >
            <button aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-black/55 backdrop-blur-sm" />
            <div className="relative tk-card max-w-3xl w-full max-h-[88vh] flex flex-col p-0 tk-menu-pop overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-black/5">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-2xl bg-[#0A84FF]/10 flex items-center justify-center shrink-0">
                            <Info className="w-5 h-5 text-[#0A84FF]" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-[#86868B]">Diagnostic Gmail</p>
                            <h3 className="text-lg font-semibold tracking-tight text-[#1D1D1F]">
                                {data.messages_found} email{data.messages_found > 1 ? "s" : ""} trouvé{data.messages_found > 1 ? "s" : ""} par la recherche
                            </h3>
                            <p className="text-xs text-[#86868B] mt-0.5">
                                {ok.length} parsé{ok.length > 1 ? "s" : ""} avec succès · {ko.length} ignoré{ko.length > 1 ? "s" : ""}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Fermer"
                        className="w-9 h-9 rounded-full hover:bg-[#F5F5F7] flex items-center justify-center text-[#86868B] hover:text-[#1D1D1F] transition shrink-0"
                        data-testid="diagnostic-modal-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                    {(data.samples || []).map((s, i) => (
                        <div
                            key={s.id || s.subject || `${s.from || "sample"}-${i}`}
                            className={`p-3 rounded-2xl border ${s.ok ? "border-[#34C759]/20 bg-[#34C759]/5" : "border-[#FF9F0A]/20 bg-[#FF9F0A]/5"}`}
                            data-testid={`diag-sample-${i}`}
                        >
                            <div className="flex items-start gap-3">
                                {s.ok ? (
                                    <CheckCircle2 className="w-4 h-4 text-[#34C759] mt-0.5 shrink-0" />
                                ) : (
                                    <AlertCircle className="w-4 h-4 text-[#FF9F0A] mt-0.5 shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-[#1D1D1F] truncate">
                                        {s.subject || "(no subject)"}
                                    </p>
                                    <p className="text-[11px] text-[#86868B] truncate mt-0.5">{s.from || "(unknown)"}</p>
                                    {s.ok ? (
                                        <p className="text-xs text-[#34C759] font-medium mt-1.5">
                                            ✓ {s.merchant} · {s.amount} {s.currency} · {s.purchase_date || "—"}
                                        </p>
                                    ) : (
                                        <p className="text-xs text-[#FF9F0A] mt-1.5">⚠ {s.reason || "Non parsé"}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    {(!data.samples || data.samples.length === 0) && (
                        <p className="text-center text-[#86868B] py-10 text-sm">
                            Aucun email ne correspond à la recherche. Vérifiez que vos reçus sont dans la boîte principale (pas archivés).
                        </p>
                    )}
                </div>

                {/* Footer */}
                <details className="border-t border-black/5 px-6 py-3 text-[11px] text-[#86868B]">
                    <summary className="cursor-pointer hover:text-[#1D1D1F]">Requête Gmail utilisée</summary>
                    <code className="block mt-2 p-2 bg-[#F5F5F7] rounded text-[10px] font-mono break-all">
                        {data.query_used}
                    </code>
                </details>
            </div>
        </div>
    );
}


// ---------- Date-range scanner modal ----------
function DateRangeScanModal({ conn, onClose, onConfirm }) {
    const today = new Date().toISOString().slice(0, 10);
    const monthAgo = new Date(Date.now() - 30 * 86400 * 1000).toISOString().slice(0, 10);
    const [since, setSince] = useState(monthAgo);
    const [until, setUntil] = useState(today);

    useEffect(() => {
        if (!conn) return;
        const esc = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", esc);
        return () => document.removeEventListener("keydown", esc);
    }, [conn, onClose]);

    if (!conn) return null;

    const presets = [
        { label: "7 derniers jours", days: 7 },
        { label: "30 derniers jours", days: 30 },
        { label: "90 derniers jours", days: 90 },
        { label: "6 derniers mois", days: 180 },
        { label: "1 dernière année", days: 365 },
    ];

    return (
        <div
            className="fixed inset-0 z-[90] flex items-center justify-center px-4 sm:px-6 tk-fade-in"
            role="dialog"
            aria-modal="true"
            data-testid="range-scan-modal"
        >
            <button aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-black/55 backdrop-blur-sm" />
            <div className="relative tk-card max-w-md w-full p-6 sm:p-7 tk-menu-pop">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-[#F5F5F7] flex items-center justify-center text-[#86868B] hover:text-[#1D1D1F] transition"
                    aria-label="Fermer"
                    data-testid="range-scan-close"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="w-11 h-11 rounded-2xl bg-[#0A84FF]/10 flex items-center justify-center mb-4">
                    <Calendar className="w-5 h-5 text-[#0A84FF]" />
                </div>
                <h3 className="text-xl font-semibold tracking-tight text-[#1D1D1F]">Scanner une période précise</h3>
                <p className="mt-2 text-sm text-[#86868B] leading-relaxed">
                    Choisissez l&apos;intervalle de dates à analyser dans votre boîte mail.
                </p>

                {/* Presets */}
                <div className="mt-5 flex flex-wrap gap-2">
                    {presets.map((p) => (
                        <button
                            key={p.label}
                            onClick={() => {
                                const start = new Date(Date.now() - p.days * 86400 * 1000).toISOString().slice(0, 10);
                                setSince(start);
                                setUntil(today);
                            }}
                            className="text-xs px-3 py-1.5 rounded-full border border-black/10 bg-white text-[#1D1D1F] hover:bg-[#F5F5F7] transition"
                            data-testid={`range-preset-${p.days}`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {/* Date inputs */}
                <div className="mt-5 grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-[#86868B] mb-1.5 block">Du</label>
                        <input
                            type="date"
                            value={since}
                            max={until || undefined}
                            onChange={(e) => setSince(e.target.value)}
                            className="tk-input"
                            data-testid="range-since"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-[#86868B] mb-1.5 block">Au</label>
                        <input
                            type="date"
                            value={until}
                            min={since || undefined}
                            max={today}
                            onChange={(e) => setUntil(e.target.value)}
                            className="tk-input"
                            data-testid="range-until"
                        />
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="tk-btn tk-btn-secondary"
                        data-testid="range-cancel"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={() => onConfirm({ since, until })}
                        disabled={!since || !until}
                        className="tk-btn tk-btn-primary disabled:opacity-50"
                        data-testid="range-confirm"
                    >
                        Lancer le scan
                    </button>
                </div>
            </div>
        </div>
    );
}




